import React, { useMemo, useEffect } from 'react';
import { usePermissionsMode } from '../contexts/PermissionsModeContext';
import UserPermissionsIcon from './icons/UserPermissionsIcon';
import TypeAhead from './TypeAhead';
import { buildEntityOptions, resolveEntity } from './permissions/entityOptions';
import {
  XMarkIcon,
  CheckIcon,
  UserGroupIcon,
  UserIcon,
} from '@heroicons/react/24/outline';

const PermissionsModeToolbar: React.FC = () => {
  const {
    permissionsMode,
    setPermissionsMode,
    enabledGroups,
    enabledUsers,
    selectedEntity,
    setSelectedEntity,
    savePermissions,
    loadingGroups,
    loadingUsers,
    bulkUpdatePermissions,
    pageComponents,
    componentPermissions,
    savedPermissions,
    permissionsBaselineLoaded,
    accessLevels,
  } = usePermissionsMode();

  // Check if we're in a test or local environment
  const environment: 'test' | 'local' | null =
    import.meta.env.VITE_ENV === 'test' ? 'test' :
    (import.meta.env.VITE_ENV === 'local' || import.meta.env.DEV) ? 'local' :
    null;

  // Enabled groups, plus enabled users once the admin types (see buildEntityOptions)
  const entityOptions = useMemo(
    () => buildEntityOptions(enabledGroups, enabledUsers),
    [enabledGroups, enabledUsers],
  );

  // Auto-select first group if none selected
  useEffect(() => {
    if (!selectedEntity && enabledGroups.length > 0) {
      const group = enabledGroups[0];
      setSelectedEntity({ type: 'group', id: group.group_id, label: group.group_name });
    }
  }, [selectedEntity, enabledGroups, setSelectedEntity]);

  // Check if there are unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    if (componentPermissions.size !== savedPermissions.size) {
      return true;
    }

    const componentPermissionsArray = Array.from(componentPermissions.entries());

    for (const [componentId, currentPerms] of componentPermissionsArray) {
      const savedPerms = savedPermissions.get(componentId);

      if (!savedPerms) {
        return true;
      }

      const currentEntityIds = Object.keys(currentPerms.permissions);
      const savedEntityIds = Object.keys(savedPerms.permissions);

      if (currentEntityIds.length !== savedEntityIds.length) {
        return true;
      }

      for (const entityId of currentEntityIds) {
        const current = currentPerms.permissions[entityId];
        const saved = savedPerms.permissions[entityId];

        if (!saved || current.accessLevel !== saved.accessLevel) {
          return true;
        }
      }
    }

    return false;
  }, [componentPermissions, savedPermissions]);

  // Calculate the current bulk access level for the selected entity
  // Shows the minimum access level across all page components (or 1 if no permissions set)
  const bulkAccessLevel = useMemo(() => {
    if (!selectedEntity || pageComponents.length === 0) {
      return 1;
    }

    let minLevel = Infinity;

    pageComponents.forEach(component => {
      const componentPerm = componentPermissions.get(component.id);
      const entityPerm = componentPerm?.permissions[selectedEntity.id];
      const level = entityPerm ? entityPerm.accessLevel : 1; // default to View if not set
      minLevel = Math.min(minLevel, level);
    });

    return minLevel === Infinity ? 1 : minLevel;
  }, [selectedEntity, pageComponents, componentPermissions]);

  // Note: useEffect for syncing bulkView/bulkEdit removed - use bulkPermissionState directly.
  // bulkUpdatePermissions updates context state immediately, so bulkPermissionState will recompute.

  // Handle entity selection change
  const handleEntityChange = (value: string) => {
    if (!value) return;
    const entity = resolveEntity(value, enabledGroups, enabledUsers);
    if (entity) setSelectedEntity(entity);
  };

  if (!permissionsMode) {
    return null;
  }

  const isLoading = loadingGroups || loadingUsers;
  const hasNoEntities = enabledGroups.length === 0 && enabledUsers.length === 0;

  return (
    <div
      className="fixed left-0 right-0 top-0 z-[100] bg-gradient-to-r from-purple-600 to-indigo-600 dark:from-purple-800 dark:to-indigo-800 shadow-lg"
    >
      <div className="px-4 py-2 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between flex-wrap gap-4">
          {/* Left side - Title and instructions */}
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <div className="flex items-center justify-center w-8 h-8 bg-white/20 rounded-full">
                <UserPermissionsIcon className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-white">Permissions Mode Active</h3>
                <p className="text-xs text-white/80">Click the handle on a purple-outlined component to configure permissions</p>
              </div>
            </div>
          </div>

          {/* Center - Entity selector and permissions checkboxes */}
          <div className="flex items-center space-x-3">
            <label className="text-xs font-medium text-white/90">Configuring</label>

            {/* Component Count Badge */}
            <div className="flex items-center justify-center min-w-[2rem] h-8 px-2 bg-white/20 rounded-md">
              <span className="text-sm font-bold text-white">{pageComponents.length}</span>
            </div>

            <label className="text-xs font-medium text-white/90">Components:</label>

            {isLoading ? (
              <div className="px-4 py-2 bg-white/10 rounded-md text-white text-sm">
                Loading...
              </div>
            ) : hasNoEntities ? (
              <div className="px-4 py-2 bg-white/10 rounded-md text-white text-sm">
                No groups or users enabled
              </div>
            ) : (
              <div className="w-64">
                <TypeAhead
                  options={entityOptions}
                  value={selectedEntity ? `${selectedEntity.type}:${selectedEntity.id}` : ''}
                  onChange={handleEntityChange}
                  placeholder="Select group or user..."
                  showSearch={true}
                  showIcons={true}
                  showDescriptions={false}
                  groupBy={true}
                  maxHeight="max-h-80"
                  className="toolbar-typeahead"
                />
              </div>
            )}

            {/* Entity Type Badge */}
            {selectedEntity && (
              <div className="flex items-center gap-1.5 px-2 py-1 bg-white/20 rounded text-xs text-white font-medium">
                {selectedEntity.type === 'user' ? (
                  <>
                    <UserIcon className="w-3.5 h-3.5" />
                    <span>User</span>
                  </>
                ) : (
                  <>
                    <UserGroupIcon className="w-3.5 h-3.5" />
                    <span>Group</span>
                  </>
                )}
              </div>
            )}

            {/* Bulk Access Level Selector */}
            <div className="flex items-center space-x-2 ml-4 pl-4 border-l border-white/30">
              <label className="text-xs text-white/90 font-medium">Access Level:</label>
              <select
                value={bulkAccessLevel}
                disabled={pageComponents.length === 0 || !selectedEntity}
                onChange={(e) => {
                  const newLevel = parseInt(e.target.value, 10);
                  if (selectedEntity && pageComponents.length > 0) {
                    bulkUpdatePermissions(
                      pageComponents,
                      selectedEntity.id,
                      selectedEntity.type,
                      newLevel
                    );
                  }
                }}
                className="px-2 py-1 bg-white/20 border border-white/30 rounded-md text-xs text-white font-medium focus:ring-2 focus:ring-white/50 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {accessLevels.map((level) => (
                  <option key={level.id} value={level.id} className="text-gray-900">
                    {level.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Right side - Actions */}
          <div className="flex items-center space-x-2">
            <button
              onClick={savePermissions}
              // Saving is also blocked while the existing-permissions baseline
              // hasn't loaded: the save posts the full in-memory state, so a
              // partial state would overwrite grants that never loaded.
              disabled={!hasUnsavedChanges || !permissionsBaselineLoaded}
              title={
                permissionsBaselineLoaded
                  ? undefined
                  : 'Existing permissions have not finished loading — saving is disabled to protect them'
              }
              className={`inline-flex items-center px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm ${
                hasUnsavedChanges
                  ? 'bg-red-600 text-white hover:bg-red-700 ring-2 ring-red-300 animate-pulse'
                  : 'bg-white text-indigo-700 hover:bg-white/90'
              }`}
            >
              <CheckIcon className="w-4 h-4 mr-1.5" />
              Save Changes
            </button>
            <button
              onClick={() => setPermissionsMode(false)}
              className="inline-flex items-center px-3 py-2 bg-white/20 hover:bg-white/30 text-white rounded-md text-sm font-medium transition-all"
            >
              <XMarkIcon className="w-5 h-5 mr-1" />
              Exit
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PermissionsModeToolbar;
